/**
 * Charge type taxonomy for order line charges.
 *
 * One list, used by every charge picker and every charge badge. There were four
 * divergent lists before this: two hardcoded pickers that disagreed with each
 * other — the purchase order table's was missing the very `fee` value its own
 * scheme applier writes — plus the org-settings `cost_types` and a separate
 * `COST_TYPES` in `types/asset.ts`. A charge added from the table rendered as
 * `shipping` there and `Shipping` in the drawer, because only one of them had a
 * label map.
 *
 * **Not org-configurable, deliberately, for now.** The `cost_types` org setting
 * exists and is served by the API, but nothing reads it — SettingsPage loads it
 * into state with no setter and writes it straight back — and its defaults
 * (`purchase`, `materials`) are asset cost-ledger concepts that make no sense on
 * an order line. Making charge types configurable means a taxonomy of their own
 * plus a Settings surface to edit it; until then a shared constant is honest
 * about what it is.
 */

export interface ChargeTypeOption {
  value: string;
  label: string;
}

/**
 * Every charge type a user can pick.
 *
 * The first four are what a sales order actually bills for. `fee` is kept
 * because the scheme applier writes it, and the rest because purchase orders
 * already use them — dropping any of those would leave historical rows with a
 * value no picker can produce and no label can render.
 */
export const CHARGE_TYPES: readonly ChargeTypeOption[] = [
  { value: "delivery", label: "Delivery" },
  { value: "documentation", label: "Documentation" },
  { value: "handling", label: "Handling" },
  { value: "restocking", label: "Restocking" },
  { value: "fee", label: "Fee" },
  { value: "shipping", label: "Shipping" },
  { value: "freight", label: "Freight" },
  { value: "prep", label: "Prep" },
  { value: "labor", label: "Labor" },
  { value: "parts", label: "Parts" },
  { value: "customs", label: "Customs" },
  { value: "inspection", label: "Inspection" },
  { value: "registration", label: "Registration" },
  { value: "other", label: "Other" },
] as const;

const LABELS: Record<string, string> = Object.fromEntries(
  CHARGE_TYPES.map((t) => [t.value, t.label]),
);

/**
 * Display label for a charge type.
 *
 * Falls back to the raw value rather than to "Other", so a row written by an
 * older build or an import still shows what it actually is.
 */
export function chargeTypeLabel(value: string): string {
  return LABELS[value] ?? value;
}

/** Default for a new charge. Sales orders bill delivery far more than anything else. */
export const DEFAULT_CHARGE_TYPE = "delivery";
